import { zodResolver } from '@hookform/resolvers/zod'
import { CheckIcon, PackageIcon, PlusIcon } from '@phosphor-icons/react'
import { useMemo } from 'react'
import { FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useAddPackage } from '@/api/packages/add-package'
import { useCheckDuplicatePackage } from '@/api/packages/check-duplicate-package'
import { useGetPackagesSettings } from '@/api/packages/get-packages-settings'
import { searchClients } from '@/api/packages/search-clients'
import { useUpdatePackage } from '@/api/packages/update-package'
import { useUpdatePackageComment } from '@/api/packages/update-package-comment'
import { FileUploadFormField, SelectFormField } from '@/components/form'
import { TextAreaFormField } from '@/components/form/text-area-form-field'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { Button } from '@/components/ui/button'
import {
  IMAGE_UPLOAD_LIMITS,
  PHOTO_IMAGE_OPTIMIZATION,
  useImageUploadErrorToast,
} from '@/components/ui/file-upload'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'
import { useSelectOptions } from '@/components/ui/select'
import { TextInput } from '@/components/ui/text-input'
import { popupFactory, toastFactory } from '@/factories'
import { getAssetLink } from '@/helpers/get-asset-link'
import { useFormSave } from '@/hooks'
import { LockedResident, PackageCarrierSchema, PackageModel, ResidentModel } from '@/models'
import { PackageDuplicateModal } from '@/pages/packages/package-duplicate-modal'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { selectClients } from '@/reducers/clientsReducer'
import { Building } from '@/reducers/types'
import { getApiErrorMessage, getResidentUnitDisplay } from '@/utils'

import {
  createPackageFormSchema,
  getCarrierOptions,
  getPerishableOptions,
  PACKAGE_IMAGE_ACCEPTED_TYPES,
  PackageFormData,
} from './package-form-modal.constants'
import * as S from './package-form-modal.styled'

export interface PackageFormProps {
  onClose: () => void
  packageData: PackageModel | null
  mode: 'create' | 'edit'
  // Note: pre-selects and locks the resident field - used when creating a package from a
  // resident's own profile, where the resident is implied by context and must not be changed.
  lockResident?: LockedResident
  // Note: overrides the globally-selected building (Redux) - needed so a resident's own
  // building is used even if a PM has a different building selected in the top nav.
  building?: Building | null
}

export const PackageFormModal = ({
  onClose,
  packageData,
  mode,
  lockResident,
  building,
}: PackageFormProps) => {
  const { t } = useTranslation(['packages'])
  const handleImageValidationError = useImageUploadErrorToast({
    acceptedTypes: PACKAGE_IMAGE_ACCEPTED_TYPES,
    ...IMAGE_UPLOAD_LIMITS,
  })

  const globalBuilding = useSelector(selectActiveBuilding)
  const activeBuilding = building ?? globalBuilding
  const residentsList = useSelector((state) => selectClients(state)) as ResidentModel[]

  const buildingId = activeBuilding?.id

  const { data: settings } = useGetPackagesSettings({ buildingId: buildingId ?? 0 })

  const { mutateAsync: addPackage } = useAddPackage()
  const { mutateAsync: updatePackage } = useUpdatePackage()
  const { mutateAsync: updatePackageComment } = useUpdatePackageComment()
  const { mutateAsync: checkDuplicate } = useCheckDuplicatePackage()

  const clientsOptions = useSelectOptions(
    {
      query: searchClients,
      requestData: (search) => ({ buildingId: buildingId ?? 0, query: search }),
      transform: (searchedClients) => {
        const clients = searchedClients ?? residentsList
        const options = clients.map((client) => ({
          label: `${client.user.firstName} ${client.user.lastName} ${getResidentUnitDisplay(client, activeBuilding)}`,
          value: client.id,
        }))

        if (packageData?.clientId && !options.some((opt) => opt.value === packageData.clientId)) {
          const unitDisplay = getResidentUnitDisplay(packageData, activeBuilding)
          options.unshift({
            label: `${packageData.residentName} ${unitDisplay}`.trim(),
            value: packageData.clientId,
          })
        } else if (lockResident && !options.some((opt) => opt.value === lockResident.id)) {
          const unitDisplay = getResidentUnitDisplay(
            {
              apartment:
                lockResident.apartment != null ? String(lockResident.apartment) : undefined,
              buildingNumber: lockResident.buildingNumber ?? undefined,
            },
            activeBuilding,
          )
          options.unshift({
            label: `${lockResident.name} ${unitDisplay}`.trim(),
            value: lockResident.id,
          })
        }

        return options
      },
    },
    {
      enabled: !!buildingId,
      queryKey: ['searchClients', buildingId],
    },
  )

  const locations = useMemo(
    () =>
      settings?.locations
        .filter((location) => !location.packageRoom)
        .map((location) => ({
          label: location.name,
          value: location.name,
        })) ?? [],
    [settings],
  )

  const packageStatus = packageData?.status
  const isSignedPackage = !!packageData?.signature
  const isReceivedPackage = packageStatus === 'Received'
  const isRequestedPackage = packageStatus === 'Requested'

  const existingImageUrl = packageData?.image
    ? getAssetLink({
        fallback: { file: 'notification_image.png' },
        file: packageData.image as string,
        path: 'chitown-images/notification',
      })
    : undefined

  const schema = useMemo(
    () =>
      createPackageFormSchema(t, {
        hasExistingImage: !!existingImageUrl,
        locationRequired: settings?.locationRequired,
        photoRequired: settings?.photoRequired,
        trackingNumberRequired: settings?.trackingNumberRequired,
      }),
    [settings, existingImageUrl, t],
  )

  const carrierOptions = getCarrierOptions(t)
  const perishableOptions = getPerishableOptions(t)

  const defaultValues = useMemo(() => {
    const rawCarrier = packageData?.carrier as string | undefined
    const carrier = rawCarrier
      ? getCarrierOptions(t).some((opt) => opt.value === rawCarrier)
        ? packageData!.carrier
        : 'other'
      : undefined

    return {
      carrier,
      comment: packageData?.comment ?? '',
      image: [],
      location: packageData?.location ?? '',
      perishable: packageData?.perishable ?? false,
      residentId: packageData?.clientId ?? lockResident?.id,
      trackingNumber: packageData?.trackingNumber ?? '',
    }
  }, [packageData, lockResident, t])

  const form = useForm<PackageFormData>({
    defaultValues,
    mode: 'onTouched',
    resolver: zodResolver(schema),
  })
  const { formState, control } = form

  const { isSubmitting } = formState
  const selectedResidentId = useWatch({ control, name: 'residentId' })

  const selectedResident = useMemo(
    () => residentsList.find((resident) => resident.id === selectedResidentId),
    [selectedResidentId, residentsList],
  )

  const selectedResidentUnitData =
    selectedResident ??
    (selectedResidentId === packageData?.clientId ? packageData : undefined) ??
    (lockResident && selectedResidentId === lockResident.id
      ? {
          apartment: lockResident.apartment != null ? String(lockResident.apartment) : undefined,
          buildingNumber: lockResident.buildingNumber ?? undefined,
        }
      : undefined)

  const createPackage = async (submitData: Record<string, unknown>) => {
    await addPackage(submitData)
    toastFactory.success(t('packages:packageFormModal.toastCreateSuccess'))
    form.reset()
    onClose()
  }

  const onSubmit: SubmitHandler<PackageFormData> = async (data) => {
    if (!buildingId) return

    try {
      const submitData = {
        buildingId,
        carrier: data.carrier || PackageCarrierSchema.enum.other,
        clientId: data.residentId,
        comment: data.comment,
        image: data.image?.[0] ?? existingImageUrl,
        location: data.location,
        perishable: data.perishable,
        trackingNumber: data.trackingNumber || '',
      }

      if (mode === 'edit' && isReceivedPackage) {
        await updatePackageComment({ comment: data.comment || '', id: packageData!.id })
        toastFactory.success(t('packages:packageFormModal.toastCommentUpdateSuccess'))
        onClose()
        return
      }

      if (mode === 'edit') {
        await updatePackage({ ...submitData, id: packageData!.id })
        toastFactory.success(t('packages:packageFormModal.toastUpdateSuccess'))
        onClose()
        return
      }

      // Note: If tracking number not passed, there is no need to check for duplicates
      if (!data.trackingNumber) {
        await createPackage(submitData)
        return
      }

      const result = await checkDuplicate({
        buildingId,
        trackingNumber: data.trackingNumber,
      })

      if (!result?.packages?.length) {
        await createPackage(submitData)
        return
      }

      onClose()
      popupFactory.open(PackageDuplicateModal, {
        duplicatePackage: result.packages[0],
        onSaveAsNew: async () => {
          try {
            await createPackage(submitData)
          } catch (error) {
            toastFactory.error(getApiErrorMessage(error))
          }
        },
      })
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const save = useFormSave({
    form,
    isEdit: mode === 'edit',
    onSubmit,
    onUnchanged: onClose,
  })

  const isResidentFieldDisabled =
    isSignedPackage || isRequestedPackage || isReceivedPackage || !!lockResident
  const isDefaultFieldDisabled = isSignedPackage || isReceivedPackage
  const isCommentFieldDisabled = isReceivedPackage ? false : isSignedPackage
  const isImageFieldDisabled = isSignedPackage || isReceivedPackage

  return (
    <Modal
      autoWidth
      title={
        mode === 'edit'
          ? t('packages:packageFormModal.title.edit')
          : t('packages:packageFormModal.title.create')
      }
      headerIcon={PackageIcon}
      footer={
        <>
          <Button use="secondary" onClick={onClose}>
            {t('packages:packageFormModal.close')}
          </Button>
          {(isReceivedPackage || !isSignedPackage) && (
            <Button loading={isSubmitting} use="primary" onClick={save}>
              {mode === 'edit'
                ? t('packages:packageFormModal.submit.save')
                : t('packages:packageFormModal.submit.create')}
              <Icon icon={mode === 'edit' ? CheckIcon : PlusIcon} />
            </Button>
          )}
        </>
      }
      onClose={onClose}
    >
      <FormProvider {...form}>
        <S.StyledForm onSubmit={save}>
          <S.Row>
            <S.FullWidthField>
              <SelectFormField<PackageFormData, number>
                {...clientsOptions.selectProps}
                searchable
                disabled={isResidentFieldDisabled}
                label={t('packages:packageFormModal.resident.label')}
                name="residentId"
                placeholder={t('packages:packageFormModal.resident.placeholder')}
                size="medium"
                type="single"
              />
            </S.FullWidthField>
            <S.ShortField>
              <TextInput
                disabled
                readOnly
                label={t('packages:packageFormModal.unit.label')}
                value={getResidentUnitDisplay(selectedResidentUnitData, activeBuilding)}
              />
            </S.ShortField>
          </S.Row>

          <S.Row>
            <S.FullWidthField>
              <TextInputFormField
                disabled={isDefaultFieldDisabled}
                label={t('packages:packageFormModal.trackingNumber.label')}
                name="trackingNumber"
                placeholder={t('packages:packageFormModal.trackingNumber.placeholder')}
              />
            </S.FullWidthField>
            <S.FullWidthField>
              <TextInputFormField
                disabled
                label={t('packages:packageFormModal.confirmationCode.label')}
                name="confirmation-code-display"
              />
            </S.FullWidthField>
          </S.Row>

          <S.HalfWidthRow>
            <div>
              <SelectFormField<PackageFormData, string>
                required
                disabled={isDefaultFieldDisabled}
                label={t('packages:packageFormModal.carrier.label')}
                name="carrier"
                options={carrierOptions}
                placeholder={t('packages:packageFormModal.selectPlaceholder')}
                size="medium"
                type="single"
              />
            </div>
            <div>
              <SelectFormField<PackageFormData, boolean>
                disabled={isDefaultFieldDisabled}
                label={t('packages:packageFormModal.perishable.label')}
                name="perishable"
                options={perishableOptions}
                size="medium"
                type="single"
              />
            </div>
          </S.HalfWidthRow>

          <TextAreaFormField
            disabled={isCommentFieldDisabled}
            label={t('packages:packageFormModal.comment.label')}
            name="comment"
            placeholder={t('packages:packageFormModal.comment.placeholder')}
            rows={5}
            maxLength={200}
          />

          <S.Row>
            <S.FullWidthField>
              <FileUploadFormField
                name="image"
                disabled={isImageFieldDisabled}
                existingImageUrl={existingImageUrl}
                layout="maximized"
                zoomable={false}
                multiple={false}
                maxFiles={1}
                acceptedTypes={PACKAGE_IMAGE_ACCEPTED_TYPES}
                {...IMAGE_UPLOAD_LIMITS}
                imageOptimization={PHOTO_IMAGE_OPTIMIZATION}
                uploadLabel={t('packages:packageFormModal.image.label')}
                onValidationError={handleImageValidationError}
              />
            </S.FullWidthField>
          </S.Row>

          {!!locations?.length && (
            <S.Row>
              <S.FullWidthField>
                <SelectFormField<PackageFormData, string>
                  disabled={isReceivedPackage}
                  label={t('packages:packageFormModal.location.label')}
                  name="location"
                  options={locations}
                  placeholder={t('packages:packageFormModal.selectPlaceholder')}
                  size="medium"
                  type="single"
                />
              </S.FullWidthField>
            </S.Row>
          )}
        </S.StyledForm>
      </FormProvider>
    </Modal>
  )
}
